import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "@/components/common/Buttons/Button";
import ExitPopup from "@/components/common/Popups/ExitPopup";
import DeviceImage from "@/components/offer/DeviceImage";
import GradingReport from "@/components/offer/GradingReport";
import PriceSection from "@/components/offer/PriceSection";
import useNavigationHelpers from "@/hooks/useNavigationHelpers";
import { publishWithTimeout, useVisionChannel } from "@/lib/ably/vision";
import logger from "@/lib/logging/logger";
import { useStore } from "@/store";

export const Route = createFileRoute("/$locale/$partner/_app/_card/_paired/offer")({
	component: Offer,
});

function Offer() {
	const { t } = useTranslation();
	const sessionId = useStore((state) => state.sessionId) as string;
	const deviceInfo = useStore((state) => state.imeiInfo?.identification.extracted_metadata);
	const displayEstimatedPrice = useStore((state) => state.config?.pagesConfig.offer.displayEstimatedPrice);
	const displayGradingReport = useStore((state) => state.config?.pagesConfig.offer.displayGradingReport);
	const { goToNextStep, isLastStep } = useNavigationHelpers();
	const { channel: ablyChannel } = useVisionChannel(sessionId);
	const [canContinue, setCanContinue] = useState(!displayEstimatedPrice);
	const [showExitPopup, setShowExitPopup] = useState(false);

	useEffect(() => {
		const publishStatus = async () => {
			try {
				await publishWithTimeout(ablyChannel, "offer-status", {
					status: displayEstimatedPrice ? "offer-presented" : "report-presented",
				});
			} catch (error) {
				logger.warning("INFO", "Could not publish message to Ably", error);
			}
		};
		publishStatus();
	}, [ablyChannel, displayEstimatedPrice]);

	const onAccept = () => {
		// With no step after the offer, the button leads home behind the same confirmation as the navbar's home button.
		if (isLastStep) {
			setShowExitPopup(true);
			return;
		}
		goToNextStep();
	};

	return (
		<div className="flex flex-col items-center justify-start h-full overflow-y-auto overflow-x-hidden grow">
			<label className="pt-4 sm:pt-12 text-darkgrey font-semibold">{deviceInfo?.brand}</label>
			<h4 className="font-semibold">
				{deviceInfo?.model} {deviceInfo?.capacity && "•"} {deviceInfo?.capacity}
			</h4>
			{deviceInfo && (
				<div className="w-full h-[224px] sm:h-[360px] shrink-0 relative m-6">
					<DeviceImage
						category={"smartphone"}
						brand={deviceInfo?.brand}
						model={deviceInfo?.model}
					/>
				</div>
			)}
			{displayEstimatedPrice && (
				<div className="w-full mb-4 sm:mb-8">
					<PriceSection onPromoValidationChange={setCanContinue} />
				</div>
			)}
			{displayGradingReport && <GradingReport />}
			<div className="sticky bottom-0 bg-fixed-buttons w-full flex flex-col-reverse sm:flex-row flex-wrap items-center justify-center mt-auto pt-8 sm:gap-2 lg:gap-4">
				<Button
					intent={"primary"}
					label={isLastStep ? t("btn.Back to home") : t("btn.Continue")}
					onClick={onAccept}
					disabled={!canContinue}
				/>
			</div>
			<ExitPopup
				show={showExitPopup}
				setShow={setShowExitPopup}
			/>
		</div>
	);
}
